
import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './components/Dashboard';
import Simulator from './components/Simulator';
import Alerts from './components/Alerts';
import Profile from './components/Profile';
import Logs from './components/Logs';
import Auth from './components/Auth';
import { supabase } from './services/supabase';
import { NavigationTab } from './types';
import { THAI_LABELS } from './constants';
import Swal from 'sweetalert2';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NavigationTab>(NavigationTab.DASHBOARD);
  const [session, setSession] = useState<any>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('theme') === 'dark';
  });
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userHeight, setUserHeight] = useState<string>('');

  // Expanded Form State
  const [logForm, setLogForm] = useState({
    date: new Date().toISOString().split('T')[0],
    weight: '', height: '', systolic: '', diastolic: '',
    triglyceride: '', cholesterol: '', hdl: '', ldl: '',
    fbs: '', ast: '', alt: ''
  });

  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    const html = document.documentElement;
    if (isDarkMode) {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const fetchProfileHeight = async (userId: string) => {
    const { data } = await supabase.from('profiles').select('height').eq('id', userId).single();
    if (data?.height) {
      const heightStr = data.height.toString();
      setUserHeight(heightStr);
      setLogForm(prev => ({ ...prev, height: heightStr }));
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        lastUserId.current = session.user.id;
        fetchProfileHeight(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      const currentUser = session?.user;
      
      if (currentUser?.id) {
        lastUserId.current = currentUser.id;
        fetchProfileHeight(currentUser.id);
      } else if (event === 'SIGNED_OUT') {
        lastUserId.current = null;
        setUserHeight('');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sync height whenever modal opens
  useEffect(() => {
    if (isLogModalOpen && userHeight) {
      setLogForm(prev => ({ ...prev, height: userHeight }));
    }
  }, [isLogModalOpen, userHeight]);

  const handleAddLog = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { date, weight, height, systolic, diastolic, triglyceride, cholesterol } = logForm;
    if (!date || !weight || !height || !systolic || !diastolic || !triglyceride || !cholesterol) {
      Swal.fire({
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกข้อมูลที่จำเป็น (*) ให้ครบถ้วน',
        icon: 'warning',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ไม่พบเซสชันผู้ใช้ กรุณาเข้าสู่ระบบใหม่');

      const w = parseFloat(weight);
      const h = parseFloat(height);
      const bmi = w / Math.pow(h / 100, 2);

      const payload = {
        user_id: user.id,
        date: logForm.date,
        weight: w,
        height: h,
        bmi: bmi,
        systolic: parseInt(systolic),
        diastolic: parseInt(diastolic),
        triglyceride: parseInt(triglyceride),
        cholesterol: parseInt(cholesterol),
        // Changed to parseFloat for lab results with 2 decimal precision
        hdl: logForm.hdl ? parseFloat(logForm.hdl) : 0,
        ldl: logForm.ldl ? parseFloat(logForm.ldl) : 0,
        fbs: logForm.fbs ? parseFloat(logForm.fbs) : null,
        ast: logForm.ast ? parseFloat(logForm.ast) : null,
        alt: logForm.alt ? parseFloat(logForm.alt) : null
      };

      const { error } = await supabase.from('health_logs').insert([payload]);
      if (error) throw error;

      Swal.fire({
        title: 'บันทึกสำเร็จ!',
        text: `BMI ของคุณคือ ${bmi.toFixed(1)}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        background: isDarkMode ? '#1e293b' : '#ffffff',
        color: isDarkMode ? '#f1f5f9' : '#1e293b'
      });
      
      setIsLogModalOpen(false);
      setLogForm({
        date: new Date().toISOString().split('T')[0],
        weight: '', 
        height: userHeight || '', // Maintain height default
        systolic: '', diastolic: '',
        triglyceride: '', cholesterol: '', hdl: '', ldl: '',
        fbs: '', ast: '', alt: ''
      });
      
      window.dispatchEvent(new CustomEvent('refreshData'));
    } catch (err: any) {
      Swal.fire({
        title: 'บันทึกไม่สำเร็จ',
        text: err.message,
        icon: 'error',
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col justify-center transition-colors duration-500">
        <Auth />
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case NavigationTab.DASHBOARD: return <Dashboard isDarkMode={isDarkMode} />;
      case NavigationTab.LOGS: return <Logs />;
      case NavigationTab.SIMULATOR: return <Simulator isDarkMode={isDarkMode} />;
      case NavigationTab.ALERTS: return <Alerts />;
      case NavigationTab.PROFILE: return <Profile onHeightChange={(h) => setUserHeight(h.toString())} />;
      default: return <Dashboard isDarkMode={isDarkMode} />;
    }
  };

  return (
    <div className="min-h-screen pb-24 max-w-lg mx-auto bg-slate-50 dark:bg-slate-950 relative shadow-2xl shadow-slate-200 dark:shadow-none transition-colors duration-500 border-x border-slate-100 dark:border-slate-800">
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-6 pt-8 pb-4 border-b border-slate-100 dark:border-slate-800 sticky top-0 z-30 transition-colors duration-500">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-black text-blue-600 dark:text-blue-400 tracking-tight">{THAI_LABELS.APP_NAME}</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setIsDarkMode(!isDarkMode)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all active:scale-95 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-amber-400' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
              {isDarkMode ? <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" /></svg> : <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>}
              <span className="text-[10px] font-black uppercase tracking-wider">{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
            <button onClick={() => setActiveTab(NavigationTab.PROFILE)} className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden border-2 border-white dark:border-slate-800 ring-2 ring-blue-50 dark:ring-blue-900/20 shadow-sm transition-transform hover:scale-105">
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${session.user.email}`} alt="Profile" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">ออนไลน์: {session.user.email?.split('@')[0]}</p>
        </div>
      </header>

      <main className="p-6 overflow-y-auto min-h-[calc(100vh-200px)]">
        {renderContent()}
      </main>

      {isLogModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsLogModalOpen(false)}></div>
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-t-[40px] sm:rounded-[40px] shadow-2xl p-6 transform transition-transform animate-in slide-in-from-bottom-10 duration-300 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 px-2">
              <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight">บันทึกข้อมูลสุขภาพ</h2>
              <button onClick={() => setIsLogModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <form onSubmit={handleAddLog} className="space-y-6 overflow-y-auto px-2 pb-6 custom-scrollbar">
              <section className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                   <div className="w-1.5 h-4 bg-blue-500 rounded-full"></div>
                   <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">ข้อมูลพื้นฐาน (Mandatory)</h3>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">วันที่ตรวจ *</label>
                  <input type="date" required value={logForm.date} onChange={e => setLogForm({...logForm, date: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-blue-500 dark:text-white text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">น้ำหนัก (กก.) *</label>
                    <input type="number" step="0.1" required placeholder="0.0" value={logForm.weight} onChange={e => setLogForm({...logForm, weight: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-blue-500 dark:text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ส่วนสูง (ซม.) *</label>
                    <input type="number" step="0.1" required placeholder="170" value={logForm.height} onChange={e => setLogForm({...logForm, height: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-blue-500 dark:text-white text-sm" />
                  </div>
                </div>
              </section>

              <section className="space-y-3 pt-2">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                   <div className="w-1.5 h-4 bg-red-500 rounded-full"></div>
                   <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">ความดันโลหิต (Mandatory)</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">SYS (บน) *</label>
                    <input type="number" required placeholder="120" value={logForm.systolic} onChange={e => setLogForm({...logForm, systolic: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-red-500 dark:text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">DIA (ล่าง) *</label>
                    <input type="number" required placeholder="80" value={logForm.diastolic} onChange={e => setLogForm({...logForm, diastolic: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-red-500 dark:text-white text-sm" />
                  </div>
                </div>
              </section>

              <section className="space-y-3 pt-2">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                   <div className="w-1.5 h-4 bg-amber-500 rounded-full"></div>
                   <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">ระดับไขมัน (Mandatory ส่วนใหญ่)</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ไตรกลีเซอไรด์ *</label>
                    <input type="number" required placeholder="150" value={logForm.triglyceride} onChange={e => setLogForm({...logForm, triglyceride: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-amber-500 dark:text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">คอเลสเตอรอล *</label>
                    <input type="number" required placeholder="200" value={logForm.cholesterol} onChange={e => setLogForm({...logForm, cholesterol: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-amber-500 dark:text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">HDL (ไขมันดี)</label>
                    <input type="number" step="0.01" placeholder="50.00" value={logForm.hdl} onChange={e => setLogForm({...logForm, hdl: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-green-500 dark:text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">LDL (ไขมันเลว)</label>
                    <input type="number" step="0.01" placeholder="130.00" value={logForm.ldl} onChange={e => setLogForm({...logForm, ldl: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none focus:border-orange-500 dark:text-white text-sm" />
                  </div>
                </div>
              </section>

              <section className="space-y-3 pt-2">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                   <div className="w-1.5 h-4 bg-indigo-500 rounded-full"></div>
                   <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">น้ำตาลและค่าตับ (Optional)</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">FBS</label>
                    <input type="number" step="0.01" placeholder="100.00" value={logForm.fbs} onChange={e => setLogForm({...logForm, fbs: e.target.value})} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl outline-none dark:text-white text-xs" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">AST</label>
                    <input type="number" step="0.01" placeholder="25.00" value={logForm.ast} onChange={e => setLogForm({...logForm, ast: e.target.value})} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl outline-none dark:text-white text-xs" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ALT</label>
                    <input type="number" step="0.01" placeholder="25.00" value={logForm.alt} onChange={e => setLogForm({...logForm, alt: e.target.value})} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl outline-none dark:text-white text-xs" />
                  </div>
                </div>
              </section>

              <div className="pt-4 sticky bottom-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md">
                <button disabled={isSubmitting} className="w-full py-5 bg-blue-600 dark:bg-blue-500 text-white font-black rounded-2xl shadow-xl shadow-blue-100 dark:shadow-none hover:bg-blue-700 active:scale-95 transition-all">
                  {isSubmitting ? 'กำลังประมวลผล...' : 'คำนวณ BMI และบันทึกข้อมูล'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-lg bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-100 dark:border-slate-800 px-6 py-4 flex justify-between items-center shadow-[0_-15px_40px_rgba(0,0,0,0.08)] z-40 transition-colors duration-500">
        <NavButton active={activeTab === NavigationTab.DASHBOARD} onClick={() => setActiveTab(NavigationTab.DASHBOARD)} icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" /></svg>} label="หน้าหลัก" />
        <NavButton active={activeTab === NavigationTab.LOGS} onClick={() => setActiveTab(NavigationTab.LOGS)} icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} label="สมุดบันทึก" />
        <div className="flex flex-col items-center">
            <button onClick={() => setIsLogModalOpen(true)} className="w-14 h-14 bg-gradient-to-tr from-blue-600 to-blue-500 dark:from-blue-500 dark:to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-blue-200 dark:shadow-blue-900/20 -mt-10 border-4 border-slate-50 dark:border-slate-950 transition-all hover:scale-110 active:scale-95 group" title="จดบันทึกใหม่"><svg className="w-8 h-8 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg></button>
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-1 uppercase">จดบันทึก</span>
        </div>
        <NavButton active={activeTab === NavigationTab.SIMULATOR} onClick={() => setActiveTab(NavigationTab.SIMULATOR)} icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>} label="ทำนาย AI" />
        <NavButton active={activeTab === NavigationTab.ALERTS} onClick={() => setActiveTab(NavigationTab.ALERTS)} icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>} label="แจ้งเตือน" />
      </nav>
    </div>
  );
};

const NavButton: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1 transition-all ${active ? 'text-blue-600 dark:text-blue-400 scale-110' : 'text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 hover:scale-105'}`}>
    <div className={`p-1 rounded-lg ${active ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>{icon}</div>
    <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
  </button>
);

export default App;
